import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button, Switch, message } from 'antd';
import * as fabric from 'fabric';
import { useDispatch } from 'react-redux';
import { updateProduct } from '../../../actions/shopifyActions';
import './Designer.scss';

const printWidth = 1600;
const printHeight = 2285;
const printAspect = printWidth / printHeight;
const printScale = 0.55;
const strokeWidth = 1.5;

const Designer = ({ title, initProductImg, templateImg, enabled }) => {
  const [objLoading, setObjLoading] = useState(false);
  const [checked, setChecked] = useState(enabled);
  const canvasRef = useRef();
  const fabricCanvasRef = useRef();
  const dispatch = useDispatch();
  const fileInputRef = useRef();
  const [isEditing, setIsEditing] = useState(false);
  const [canvasReady, setCanvasReady] = useState(false);
  const [productImg, setProductImg] = useState(initProductImg);
  const imgRef = useRef();
  const rectRef = useRef();
  const borderRef = useRef();
  const objRef = useRef();
  const fileRef = useRef();

  const loadImg = useCallback(async url => {
    try {
      const img = await fabric.FabricImage.fromURL(url, {
        crossOrigin: 'anonymous',
      });
      return img;
    } catch (error) {
      throw new Error('Error loading image');
    }
  }, []);

  const loadTemplateImg = useCallback(async url => {
    try {
      // First attempt with crossOrigin anonymous
      const img = await fabric.FabricImage.fromURL(url, {
        crossOrigin: 'anonymous',
      });
      console.log('loadTemplateImg: Successfully loaded image with CORS');
      return img;
    } catch (error) {
      console.warn('loadTemplateImg: CORS failed, trying without:', error);

      // Second attempt without crossOrigin (will work for display but may fail on export)
      try {
        const img = await fabric.FabricImage.fromURL(url);
        console.log('loadTemplateImg: Successfully loaded image (no CORS)');

        // Mark this image as potentially problematic for export
        img._corsIssue = true;
        return img;
      } catch (fallbackError) {
        console.error('loadTemplateImg: All attempts failed:', fallbackError);
        throw new Error('Error loading template image');
      }
    }
  }, []);

  const update = useCallback(async () => {
    if (
      !fabricCanvasRef.current ||
      !imgRef.current ||
      !rectRef.current ||
      !borderRef.current
    ) {
      return;
    }

    // Canvas
    fabricCanvasRef.current.setWidth(imgRef.current.offsetWidth);
    fabricCanvasRef.current.setHeight(imgRef.current.offsetHeight);

    // Background image
    const img = fabricCanvasRef.current.backgroundImage;
    if (img) {
      img.set({
        scaleX: fabricCanvasRef.current.width / img.width,
        scaleY: fabricCanvasRef.current.height / img.height,
      });
    }

    // Rect
    const baseDim =
      Math.min(fabricCanvasRef.current.width, fabricCanvasRef.current.height) *
      printScale;
    let rectWidth = printAspect > 1 ? baseDim : baseDim * printAspect;
    let rectHeight = printAspect > 1 ? baseDim / printAspect : baseDim;
    const oldWidth = rectRef.current.width || 1; // Prevent division by zero
    rectRef.current.set({
      left: (fabricCanvasRef.current.width - rectWidth) / 2,
      top: (fabricCanvasRef.current.height - rectHeight) / 2,
      width: rectWidth,
      height: rectHeight,
    });
    const delta = rectRef.current.width / oldWidth;

    // Border
    borderRef.current.set({
      left:
        (fabricCanvasRef.current.width - rectRef.current.width - strokeWidth) /
        2,
      top:
        (fabricCanvasRef.current.height -
          rectRef.current.height -
          strokeWidth) /
        2,
      width: rectRef.current.width + strokeWidth,
      height: rectRef.current.height + strokeWidth,
    });

    // Obj
    if (objRef.current) {
      objRef.current.left *= delta;
      objRef.current.top *= delta;
      objRef.current.scaleX *= delta;
      objRef.current.scaleY *= delta;
    }

    fabricCanvasRef.current.renderAll();
  }, []);

  const create = useCallback(async () => {
    console.log('create() function started');

    // Dispose existing canvas if it exists
    if (fabricCanvasRef.current) {
      try {
        fabricCanvasRef.current.dispose();
      } catch (error) {
        console.warn('Error disposing existing canvas:', error);
      }
      fabricCanvasRef.current = null;
    }

    console.log('About to create fabric canvas');
    // Canvas
    fabricCanvasRef.current = new fabric.Canvas(canvasRef.current);
    fabricCanvasRef.current.stateful = true;

    console.log('About to load template image:', templateImg);
    // Background image (use template loader for Shopify images)
    try {
      const img = await loadTemplateImg(templateImg);
      fabricCanvasRef.current.backgroundImage = img;
      fabricCanvasRef.current.renderAll();
      console.log('Template image loaded and set as background');
    } catch (error) {
      console.error('Failed to load template image:', error);
      message.error('Failed to load template image. Please try again.');
      setObjLoading(false);
      setIsEditing(false);
      setCanvasReady(false);
      return;
    }

    // Rect
    rectRef.current = new fabric.Rect({
      fill: 'transparent',
      selectable: false,
    });
    fabricCanvasRef.current.add(rectRef.current);

    // Border
    borderRef.current = new fabric.Rect({
      fill: 'transparent',
      selectable: false,
      stroke: 'gray',
      strokeWidth: strokeWidth,
      strokeDashArray: [5, 5],
    });
    fabricCanvasRef.current.add(borderRef.current);

    await update();

    // Mark canvas as ready after basic setup
    setCanvasReady(true);

    // Obj
    setObjLoading(true);
    console.log('About to read file:', fileRef.current);

    if (!fileRef.current) {
      console.error('No file in fileRef.current');
      setObjLoading(false);
      message.error('No file selected. Please try again.');
      setIsEditing(false);
      setCanvasReady(false);
      return;
    }

    const reader = new FileReader();
    await new Promise((resolve, reject) => {
      reader.onload = async f => {
        try {
          console.log('FileReader onload triggered');
          const data = f.target.result;
          const img = await loadImg(data);
          const maxScale = 0.6;
          const scale = Math.min(
            (rectRef.current.width * maxScale) / img.width,
            (rectRef.current.height * maxScale) / img.height
          );

          img.set({
            scaleX: scale,
            scaleY: scale,
            left: fabricCanvasRef.current.width / 2,
            top: fabricCanvasRef.current.height / 2,
            originX: 'center',
            originY: 'center',
          });

          objRef.current = img;
          fabricCanvasRef.current.add(img);

          setObjLoading(false);
          console.log('Image successfully added to canvas');
          resolve(img);
        } catch (error) {
          console.error('Error in FileReader onload:', error);
          message.error(
            'Failed to process the selected image. Please try again.'
          );
          setObjLoading(false);
          setIsEditing(false);
          setCanvasReady(false);
          reject(error);
        }
      };

      reader.onerror = error => {
        console.error('FileReader error:', error);
        message.error('Failed to read the selected image file.');
        setObjLoading(false);
        setIsEditing(false);
        setCanvasReady(false);
        reject(error);
      };

      console.log('Starting to read file as data URL');
      reader.readAsDataURL(fileRef.current);
    });
  }, [loadImg, loadTemplateImg, templateImg, update]);

  const handleObjMoving = useCallback(async e => {
    const obj = e.target;

    // If object is too big, ignore
    if (
      obj.currentHeight > rectRef.current.height || // Adjusted for buffer
      obj.currentWidth > rectRef.current.width // Adjusted for buffer
    ) {
      return;
    }

    obj.setCoords();

    // Top left
    if (
      obj.getBoundingRect().top < rectRef.current.top ||
      obj.getBoundingRect().left < rectRef.current.left
    ) {
      obj.top = Math.max(
        obj.top,
        obj.top - obj.getBoundingRect().top + rectRef.current.top
      );
      obj.left = Math.max(
        obj.left,
        obj.left - obj.getBoundingRect().left + rectRef.current.left
      );
    }

    // Bottom right
    if (
      obj.getBoundingRect().top + obj.getBoundingRect().height >
        rectRef.current.top + rectRef.current.height ||
      obj.getBoundingRect().left + obj.getBoundingRect().width >
        rectRef.current.left + rectRef.current.width
    ) {
      obj.top = Math.min(
        obj.top,
        rectRef.current.top +
          rectRef.current.height -
          obj.getBoundingRect().height / 2
      );
      obj.left = Math.min(
        obj.left,
        rectRef.current.left +
          rectRef.current.width -
          obj.getBoundingRect().width / 2
      );
    }
  }, []);

  useEffect(() => {
    let resizeObserver;

    const setup = async () => {
      await create();

      resizeObserver = new ResizeObserver(() => {
        update();
      });
      resizeObserver.observe(imgRef.current);

      fabricCanvasRef.current.on('object:moving', handleObjMoving);

      // TODO: Modularize this out
      const brOld = {};
      fabricCanvasRef.current.on('object:scaling', function (e) {
        const obj = e.target;
        obj.setCoords();
        let brNew = obj.getBoundingRect();

        const inBounds =
          brNew.width + brNew.left <
            rectRef.current.width + rectRef.current.left &&
          brNew.height + brNew.top <
            rectRef.current.height + rectRef.current.top &&
          brNew.left > rectRef.current.left &&
          brNew.top > rectRef.current.top;

        if (brOld.left !== undefined && !inBounds) {
          obj.left = brOld.left;
          obj.top = brOld.top;
          obj.scaleX = brOld.scaleX;
          obj.scaleY = brOld.scaleY;
          obj.width = brOld.width;
          obj.height = brOld.height;
        } else {
          brOld.left = obj.left;
          brOld.top = obj.top;
          brOld.scaleX = obj.scaleX;
          brOld.scaleY = obj.scaleY;
          brOld.width = obj.width;
          brOld.height = obj.height;
        }
      });
    };

    console.log('useEffect triggered, isEditing:', isEditing);
    if (isEditing) {
      console.log('About to call setup');
      setup();
    }

    // Clean up
    let currentImgRef = imgRef.current;
    return () => {
      if (resizeObserver) {
        resizeObserver.unobserve(currentImgRef);
      }
      if (fabricCanvasRef.current) {
        try {
          // Remove all event listeners first
          fabricCanvasRef.current.off('object:moving');
          fabricCanvasRef.current.off('object:scaling');
          // Clear all objects
          fabricCanvasRef.current.clear();
          // Dispose canvas
          fabricCanvasRef.current.dispose();
        } catch (error) {
          console.warn('Error disposing canvas:', error);
        }
        fabricCanvasRef.current = null;
      }
      // Reset refs
      rectRef.current = null;
      borderRef.current = null;
      objRef.current = null;
    };
  }, [create, handleObjMoving, isEditing, update]);

  const handleSwitchChange = useCallback(
    async checked => {
      try {
        console.log('About to dispatch updateProduct for switch change:', {
          enabled: checked,
          title,
        });
        dispatch(updateProduct({ enabled: checked, title }));
        setChecked(checked);
      } catch (error) {
        console.error('Error updating product enabled status:', error);
        message.error('Failed to update product status. Please try again.');
        // Revert switch to previous state
        setChecked(!checked);
      }
    },
    [dispatch, title]
  );

  const handleInputChange = e => {
    const file = e.target.files[0];

    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', file.name, file.type, file.size);

    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG file!');
      return;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Image must smaller than 2MB!');
      return;
    }

    fileRef.current = file;
    console.log('Setting isEditing to true, fileRef.current:', fileRef.current);

    setIsEditing(true);
  };

  const handleEditCancel = () => {
    if (isEditing) {
      // Clean up canvas
      if (fabricCanvasRef.current) {
        try {
          fabricCanvasRef.current.off('object:moving');
          fabricCanvasRef.current.off('object:scaling');
          fabricCanvasRef.current.clear();
          fabricCanvasRef.current.dispose();
        } catch (error) {
          console.warn('Error disposing canvas in handleEditCancel:', error);
        }
        fabricCanvasRef.current = null;
      }
      // Reset refs
      rectRef.current = null;
      borderRef.current = null;
      objRef.current = null;
      setIsEditing(false);
      setCanvasReady(false);
    } else {
      fileInputRef.current.click();
      // Is editing triggered on successful image upload
    }
  };

  const handleSave = () => {
    // Check if canvas is ready
    if (!canvasReady) {
      console.error('Canvas is not ready for saving');
      message.error(
        'Canvas not ready. Please wait for the image to load completely.'
      );
      return;
    }

    // Check if canvas exists and is valid
    if (!fabricCanvasRef.current || !fabricCanvasRef.current.remove) {
      console.error('Canvas is not available for saving');
      message.error('Canvas not available. Please try reloading the designer.');
      return;
    }

    // Check if all required refs exist
    if (!rectRef.current || !borderRef.current || !imgRef.current) {
      console.error('Required canvas elements are not available for saving');
      message.error(
        'Canvas elements missing. Please try reloading the designer.'
      );
      return;
    }

    // Check if user image exists
    if (!objRef.current) {
      console.error('No user image to save');
      message.error('No image to save. Please upload an image first.');
      return;
    }

    // Store original background image
    const originalBgImage = fabricCanvasRef.current.backgroundImage;

    // Remove border for export
    fabricCanvasRef.current.remove(borderRef.current);

    // Export main image with background
    let mainImgUrl;
    try {
      // Check if background image has CORS issues
      const hasCorsIssue = originalBgImage && originalBgImage._corsIssue;

      if (hasCorsIssue) {
        console.warn(
          'Background image has CORS restrictions, exporting without background for main image'
        );
        // Temporarily remove background to avoid CORS taint
        fabricCanvasRef.current.backgroundImage = null;
        fabricCanvasRef.current.renderAll();
      } else if (originalBgImage) {
        fabricCanvasRef.current.backgroundImage = originalBgImage;
        fabricCanvasRef.current.renderAll();
      }

      // Calculate multiplier safely
      const canvasWidth = fabricCanvasRef.current.width;
      const naturalWidth = imgRef.current.naturalWidth;
      let mainMultiplier =
        naturalWidth && canvasWidth ? naturalWidth / canvasWidth : 1;

      // Validate multiplier
      if (!isFinite(mainMultiplier) || mainMultiplier <= 0) {
        console.warn('Invalid multiplier, using default value of 1');
        mainMultiplier = 1;
      }

      // Try export with multiplier first
      try {
        mainImgUrl = fabricCanvasRef.current.toDataURL({
          multiplier: mainMultiplier,
          format: 'png',
          quality: 1.0,
        });
      } catch (corsError) {
        console.warn('CORS error with multiplier, trying without:', corsError);
        try {
          mainImgUrl = fabricCanvasRef.current.toDataURL({
            format: 'png',
            quality: 1.0,
          });
        } catch (fallbackError) {
          console.error('Both export attempts failed:', fallbackError);
          throw new Error('Canvas export failed due to security restrictions');
        }
      }

      // Validate the exported image
      if (!mainImgUrl || !mainImgUrl.startsWith('data:image/')) {
        throw new Error('Invalid image data generated');
      }

      // If we removed background due to CORS, show a warning
      if (hasCorsIssue) {
        message.warning(
          'Exported without background template due to security restrictions. Only your design will be saved.'
        );
      }

      setProductImg(mainImgUrl);
    } catch (error) {
      console.error('Error exporting main image:', error);
      message.error(
        'Failed to export main image. Please try again or use a different browser.'
      );

      // Re-add border and restore state
      try {
        if (originalBgImage) {
          fabricCanvasRef.current.backgroundImage = originalBgImage;
        }
        fabricCanvasRef.current.add(borderRef.current);
        fabricCanvasRef.current.renderAll();
      } catch (restoreError) {
        console.warn('Error restoring canvas state:', restoreError);
      }
      return;
    }

    // Export print image - create version without background
    let printImgUrl;
    try {
      // Temporarily remove background for print area export
      fabricCanvasRef.current.backgroundImage = null;
      fabricCanvasRef.current.renderAll();

      // Calculate print multiplier safely
      const rectWidth = rectRef.current.width;
      let printMultiplier =
        rectWidth && rectWidth > 0 ? printWidth / rectWidth : 1;

      // Validate print multiplier
      if (!isFinite(printMultiplier) || printMultiplier <= 0) {
        console.warn('Invalid print multiplier, using default value of 1');
        printMultiplier = 1;
      }

      // Validate crop dimensions
      const cropLeft = rectRef.current.left;
      const cropTop = rectRef.current.top;
      const cropWidth = rectRef.current.width;
      const cropHeight = rectRef.current.height;

      if (cropLeft < 0 || cropTop < 0 || cropWidth <= 0 || cropHeight <= 0) {
        throw new Error('Invalid crop dimensions');
      }

      // Try export with multiplier first
      try {
        printImgUrl = fabricCanvasRef.current.toDataURL({
          left: cropLeft,
          top: cropTop,
          width: cropWidth,
          height: cropHeight,
          multiplier: printMultiplier,
          format: 'png',
          quality: 1.0,
        });
      } catch (corsError) {
        console.warn(
          'CORS error with multiplier for print, trying without:',
          corsError
        );
        try {
          printImgUrl = fabricCanvasRef.current.toDataURL({
            left: cropLeft,
            top: cropTop,
            width: cropWidth,
            height: cropHeight,
            format: 'png',
            quality: 1.0,
          });
        } catch (fallbackError) {
          console.error('Both print export attempts failed:', fallbackError);
          throw new Error(
            'Print area export failed due to security restrictions'
          );
        }
      }

      // Validate the exported print image
      if (!printImgUrl || !printImgUrl.startsWith('data:image/')) {
        throw new Error('Invalid print image data generated');
      }
    } catch (error) {
      console.error('Error exporting print image:', error);
      message.error(
        'Failed to export print area. Please try again or contact support.'
      );

      // Re-add border and restore background
      try {
        if (originalBgImage) {
          fabricCanvasRef.current.backgroundImage = originalBgImage;
        }
        fabricCanvasRef.current.add(borderRef.current);
        fabricCanvasRef.current.renderAll();
      } catch (restoreError) {
        console.warn(
          'Error restoring canvas state after print export failure:',
          restoreError
        );
      }
      return;
    }

    // Extract base64 data and validate
    const mainImgParts = mainImgUrl.split(',');
    const printImgParts = printImgUrl.split(',');

    if (mainImgParts.length !== 2 || printImgParts.length !== 2) {
      console.error('Invalid image data format');
      message.error('Invalid image format generated. Please try again.');

      // Restore canvas state
      try {
        if (originalBgImage) {
          fabricCanvasRef.current.backgroundImage = originalBgImage;
        }
        fabricCanvasRef.current.add(borderRef.current);
        fabricCanvasRef.current.renderAll();
      } catch (restoreError) {
        console.warn('Error restoring canvas state:', restoreError);
      }
      return;
    }

    const mainImg = mainImgParts[1];
    const printImg = printImgParts[1];

    // Validate base64 data
    if (
      !mainImg ||
      !printImg ||
      mainImg.length < 100 ||
      printImg.length < 100
    ) {
      console.error(
        'Generated image data appears to be corrupted or too small'
      );
      message.error('Generated images appear corrupted. Please try again.');

      // Restore canvas state
      try {
        if (originalBgImage) {
          fabricCanvasRef.current.backgroundImage = originalBgImage;
        }
        fabricCanvasRef.current.add(borderRef.current);
        fabricCanvasRef.current.renderAll();
      } catch (restoreError) {
        console.warn('Error restoring canvas state:', restoreError);
      }
      return;
    }

    console.log('Successfully generated images for save:', {
      title,
      mainImgLength: mainImg.length,
      printImgLength: printImg.length,
      mainImgPreview: mainImg.substring(0, 50) + '...',
      printImgPreview: printImg.substring(0, 50) + '...',
    });

    // Show success message and dispatch update
    message.success('Images generated successfully. Saving to server...');
    dispatch(updateProduct({ mainImg, printImg, title }));

    // Clean up canvas
    try {
      fabricCanvasRef.current.off('object:moving');
      fabricCanvasRef.current.off('object:scaling');
      fabricCanvasRef.current.clear();
      fabricCanvasRef.current.dispose();
    } catch (error) {
      console.warn('Error disposing canvas in handleSave:', error);
    }
    fabricCanvasRef.current = null;

    // Reset refs
    rectRef.current = null;
    borderRef.current = null;
    objRef.current = null;

    setIsEditing(false);
    setCanvasReady(false);
  };

  return (
    <div className="designer">
      <div className="configurer">
        <img ref={imgRef} className="shirt" src={productImg} alt="merch" />
        {isEditing && <canvas ref={canvasRef} className="shirt" />}
      </div>
      <div className="side-wrapper">
        <div className="enable-text">
          Enable Sales on <b>My Page</b>
        </div>
        <div>
          <Switch
            checked={checked}
            onChange={handleSwitchChange}
            className="switch"
          />
        </div>
        <div className="designer-btns">
          <Button type="secondary" onClick={handleEditCancel}>
            {isEditing ? 'Cancel' : 'Edit'}
          </Button>
          {isEditing && (
            <Button
              type="primary"
              onClick={handleSave}
              disabled={!canvasReady || objLoading}
            >
              {objLoading ? 'Loading...' : 'Save'}
            </Button>
          )}
        </div>
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleInputChange}
          onClick={() => (fileInputRef.current.value = null)}
        />
      </div>
    </div>
  );
};

export default Designer;
